import React, { useState, useEffect } from 'react';
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Button,
  Row,
  Col,
  Alert,
  Input,
} from 'reactstrap';
import DataTable from '../../components/DataTable';
import ConfirmModal from '../../components/ConfirmModal';
import Loading from '../../components/Loading';
import { getIcon } from '../../utils/iconMapper';
import PermisoForm from './PermisoForm';
import { permisosService } from '../../services/permisosService';
import SnowFallComponent from 'components/SnowFallComponent';

function Permisos() {
  const [permisos, setPermisos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingPermiso, setEditingPermiso] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [permisoToDelete, setPermisoToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [filtroModulo, setFiltroModulo] = useState('');
  const [modulosDisponibles, setModulosDisponibles] = useState([]);

  useEffect(() => {
    loadPermisos();
  }, [filtroModulo]);

  const loadPermisos = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await permisosService.getAll(filtroModulo || null);

      if (response.success && response.data) {
        const permisosData = Array.isArray(response.data) ? response.data : [];
        const normalized = permisosData.map(permiso => ({
          PermisoID: permiso.PermisoID || permiso.permisoID,
          Nombre: permiso.Nombre || permiso.nombre || '',
          Descripcion: permiso.Descripcion || permiso.descripcion,
          Modulo: permiso.Modulo || permiso.modulo || '',
          Accion: permiso.Accion || permiso.accion || '',
        }));
        setPermisos(normalized);

        // Extraer módulos únicos para el filtro
        const modulos = [...new Set(normalized.map(p => p.Modulo).filter(Boolean))].sort();
        setModulosDisponibles(modulos);
      } else {
        setError(response.Message || response.message || 'Error al cargar permisos');
      }
    } catch (err) {
      setError(err.message || 'Error al conectar con el servidor');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingPermiso(null);
    setShowForm(true);
  };

  const handleEdit = async (permiso) => {
    try {
      setLoading(true);
      const response = await permisosService.getById(permiso.PermisoID || permiso.permisoID);
      if (response.success && response.data) {
        setEditingPermiso(response.data);
        setShowForm(true);
      } else {
        setError(response.Message || response.message || 'Error al cargar el permiso');
      }
    } catch (err) {
      setError(err.message || 'Error al conectar con el servidor');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (permiso) => {
    setPermisoToDelete(permiso);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      setDeleting(true);
      const permisoId = permisoToDelete.PermisoID || permisoToDelete.permisoID;
      const response = await permisosService.delete(permisoId);

      if (response.success) {
        setSuccess('Permiso eliminado exitosamente');
        setTimeout(() => setSuccess(''), 3000);
        setShowDeleteModal(false);
        setPermisoToDelete(null);
        loadPermisos();
      } else {
        setError(response.Message || response.message || 'Error al eliminar el permiso');
      }
    } catch (err) {
      setError(err.message || 'Error al conectar con el servidor');
    } finally {
      setDeleting(false);
    }
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingPermiso(null);
    setSuccess('Permiso guardado exitosamente');
    setTimeout(() => setSuccess(''), 3000);
    loadPermisos();
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingPermiso(null);
  };

  const columns = [
    {
      key: 'nombre',
      label: 'Nombre',
      accessor: 'Nombre',
    },
    {
      key: 'modulo',
      label: 'Módulo',
      accessor: 'Modulo',
    },
    {
      key: 'accion',
      label: 'Acción',
      accessor: 'Accion',
    },
    {
      key: 'descripcion',
      label: 'Descripción',
      accessor: 'Descripcion',
      render: (value) => value || '-',
    },
    {
      key: 'actions',
      label: 'Acciones',
      render: (_, row) => (
        <div>
          <Button
            color="info"
            size="sm"
            className="mr-2"
            onClick={(e) => {
              e.stopPropagation();
              handleEdit(row);
            }}
            title="Editar"
          >
            <i className={getIcon("tim-icons icon-pencil")} />
          </Button>
          <Button
            color="danger"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(row);
            }}
            title="Eliminar"
          >
            <i className={getIcon("tim-icons icon-simple-remove")} />
          </Button>
        </div>
      ),
    },
  ];

  if (showForm) {
    return (
      <PermisoForm
        permiso={editingPermiso}
        onSuccess={handleFormSuccess}
        onCancel={handleFormCancel}
      />
    );
  }

  return (
    <div className="content">
      <SnowFallComponent />
      <Row>
        <Col md="12">
          <Card>
            <CardHeader>
              <Row>
                <Col>
                  <CardTitle tag="h4">Permisos</CardTitle>
                </Col>
                <Col className="text-right">
                  <Button color="primary" onClick={handleCreate}>
                    <i className={getIcon("tim-icons icon-simple-add")} /> Nuevo Permiso
                  </Button>
                </Col>
              </Row>
              <Row className="mt-3">
                <Col md="4">
                  <Input
                    type="select"
                    value={filtroModulo}
                    onChange={(e) => setFiltroModulo(e.target.value)}
                  >
                    <option value="">Todos los módulos</option>
                    {modulosDisponibles.map((modulo) => (
                      <option key={modulo} value={modulo}>
                        {modulo}
                      </option>
                    ))}
                  </Input>
                </Col>
              </Row>
            </CardHeader>
            <CardBody>
              {error && (
                <Alert color="danger" className="mb-3">
                  {error}
                </Alert>
              )}
              {success && (
                <Alert color="success" className="mb-3">
                  {success}
                </Alert>
              )}

              {loading ? (
                <Loading />
              ) : (
                <DataTable columns={columns} data={permisos} />
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      <ConfirmModal
        isOpen={showDeleteModal}
        toggle={() => {
          setShowDeleteModal(false);
          setPermisoToDelete(null);
        }}
        title="Eliminar Permiso"
        message="¿Está seguro de eliminar este permiso? No se puede eliminar si tiene roles asignados."
        confirmText="Eliminar"
        cancelText="Cancelar"
        onConfirm={confirmDelete}
        loading={deleting}
      />
    </div>
  );
}

export default Permisos;

